import { AssetManager, consumer, inject, Scene } from "@spawn/core";
import { NudgingSymbolMetaDataKeys } from "@spawn/nudge";
import { RevealSymbolMetaDataKeys } from "@spawn/reveal";
import { BackgroundView, CommonAssets, GameScene, LoadingScene, SlotMachinePresets, SymbolMetaData } from "@spawn/slot-machine";
import { DarkLoadingScene, NrgBackgroundView, FreegameOutroView, NrgGame, SymbolTypes } from "@spawn/nrg-games-core";
import { SymbolService } from "./services/symbolService";
import { DesignView } from "./views/designView";
import { FreegameIntroView } from "./views/freegames/freegameIntroView";
import { LoadingScreenFeatureInfo } from "./views/loadingScreen/loadingScreenFeatureInfo";
import { LogoSlideView } from "./views/loadingScreen/loadingScreenLogo";

@consumer
export class Game extends NrgGame {
	@inject protected readonly symbolService!: SymbolService;

	protected freegameIntroView(): typeof FreegameIntroView {
		return FreegameIntroView;
	}

	protected freegameOutroView(): typeof FreegameOutroView {
		return FreegameOutroView;
	}

	protected override symbolMetaData(symbolType: SymbolTypes): SymbolMetaData {
		return {
			...super.symbolMetaData(symbolType),
			[RevealSymbolMetaDataKeys.UncoverAnimationNormal]: this.symbolService.getUncoverAnimation(symbolType),
			[NudgingSymbolMetaDataKeys.NudgeAnimation]: this.symbolService.getNudgeAnimation(symbolType),
		};
	}

	override async createLoadingScene(): Promise<Scene> {
		const animationDescriptor = AssetManager.createDescriptor(LoadingScreenFeatureInfo, "bonusSpine", "introscreen_bonus.json");

		await this.assetManager.addAssets(
			...SlotMachinePresets.loadingScene(this.networkService.casinoOptions.branding),
			AssetManager.createDescriptor(LoadingScene, "landscapeBackground", "background_landscape_bg.png"),
			AssetManager.createDescriptor(LoadingScene, "portraitBackground", "background_portrait_bg.png"),
			AssetManager.createDescriptor(LogoSlideView, "loadingChar", "loading_char.png"),
			AssetManager.createDescriptor(CommonAssets, "fontModern", "@spawn/core-assets:LexendDeca.css", "Lexend Deca"),
			AssetManager.createDescriptor(LogoSlideView, "logoTexture", "logo_twoliner.png"),
			...AssetManager.createMultipleDescriptors(
				BackgroundView,
				"landscapeTextures",
				["background_landscape_bg.png"],
				["background_landscape_fg.png"]
			),
			...AssetManager.createMultipleDescriptors(
				BackgroundView,
				"portraitTextures",
				["background_portrait_bg.png"],
				["background_portrait_fg.png"]
			),
			animationDescriptor,
			AssetManager.createDescriptor(NrgBackgroundView, "backgroundAnimations", "background_idle.json", animationDescriptor),
			AssetManager.createDescriptor(LoadingScreenFeatureInfo, "nudgingSpine", "introscreen_nudging.json", animationDescriptor),
			AssetManager.createDescriptor(LoadingScreenFeatureInfo, "scatterSpine", "introscreen_scatter.json", animationDescriptor)
		);

		return new DarkLoadingScene([new LogoSlideView(), new LoadingScreenFeatureInfo()]);
	}

	override createGameScene(): Scene {
		return new GameScene(new DesignView());
	}
}
